import { useState } from 'react'
import { AlertTriangle, RotateCw, ChevronDown, Trash2 } from 'lucide-react'
import { Button } from '@/ui/Button'
import { hardReset } from '@/lib/boot'
import { recentEvents } from '@/lib/telemetry'

interface Props {
  error: Error
  scope: 'app' | 'route'
  onDismiss: () => void
}

/**
 * What the user sees when something breaks.
 *
 * The point of this screen is that it is never a dead end. Previously a crash
 * produced a blank white page or Safari's "cannot open the page", from which
 * the only recourse was force-quitting the app. Here there is always a next
 * action, ordered from least to most destructive: go back, reload, reset.
 */
export function RecoveryScreen({ error, scope, onDismiss }: Props) {
  const [showDetail, setShowDetail] = useState(false)
  const [resetting, setResetting] = useState(false)

  const isRoute = scope === 'route'

  return (
    <div
      role="alert"
      className="inset-safe flex min-h-dvh flex-col items-center justify-center gap-6 px-6 py-10 text-center"
    >
      <div className="grid h-16 w-16 place-items-center rounded-full bg-bad-50 text-bad-600">
        <AlertTriangle className="h-8 w-8" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        <h1 className="text-xl font-bold tracking-tight">
          {isRoute ? 'This screen ran into a problem' : "ScanFlow ran into a problem"}
        </h1>
        <p className="mx-auto max-w-sm text-[0.9375rem] leading-relaxed text-[var(--text-secondary)]">
          {isRoute
            ? 'The rest of the app is still working. Go back and try again.'
            : 'Reloading usually fixes it. Your scans are stored on the server, so nothing already submitted is lost.'}
        </p>
      </div>

      <div className="flex w-full max-w-xs flex-col gap-3">
        {isRoute && (
          <Button variant="primary" size="lg" fullWidth onClick={onDismiss}>
            Go back
          </Button>
        )}
        <Button
          variant={isRoute ? 'secondary' : 'primary'}
          size="lg"
          fullWidth
          leadingIcon={<RotateCw className="h-4 w-4" aria-hidden="true" />}
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
        <Button
          variant="ghost"
          fullWidth
          busy={resetting}
          leadingIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
          onClick={() => {
            setResetting(true)
            void hardReset()
          }}
        >
          Reset app data
        </Button>
      </div>

      <div className="w-full max-w-sm">
        <button
          type="button"
          onClick={() => setShowDetail((v) => !v)}
          aria-expanded={showDetail}
          className="mx-auto flex min-h-touch items-center gap-1.5 px-3 text-sm text-[var(--text-muted)]"
        >
          Technical details
          <ChevronDown
            className={`h-4 w-4 transition-transform ${showDetail ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {showDetail && (
          <div className="mt-2 space-y-2 text-left">
            <pre className="max-h-48 overflow-auto rounded-xl bg-[var(--surface-sunken)] p-3 text-xs leading-relaxed text-[var(--text-secondary)]">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
            {recentEvents().length > 0 && (
              <pre className="max-h-32 overflow-auto rounded-xl bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-muted)]">
                {recentEvents()
                  .slice(-5)
                  .map((e) => `${e.at.slice(11, 19)}  ${e.event}  ${e.message ?? ''}`)
                  .join('\n')}
              </pre>
            )}
            <p className="px-1 text-xs text-[var(--text-muted)]">Build {__APP_VERSION__}</p>
          </div>
        )}
      </div>
    </div>
  )
}
